import React from "react";
import {
  Briefcase, ShieldCheck, Users, Network, ArrowRight, AlertTriangle,
  ScanSearch, ClipboardList, Building2, KeyRound, TrendingDown, HeartPulse, Quote,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import { Eyebrow, RouteButton } from "@/components/Bits";
import { IMAGES } from "@/lib/site";

const thesisBreakdowns = [
  "Key customer and supplier relationships are held exclusively by the owner.",
  "Institutional and operational knowledge lives entirely in the founder’s head.",
  "Employees rely on informal, undocumented direction rather than systems.",
  "The corporate culture is tied strictly to one individual’s presence.",
  "Middle management layers are weak, un-incentivized, or unprepared.",
];

const services = [
  {
    name: "Succession Roadmap Sprint",
    audience: "Business Owners / Founders",
    objective: "Identify exit options & fix founder dependency before selling.",
    deliverables: ["Founder Dependency Snapshot", "Succession Pathway Map", "90-Day Operational Action Plan"],
    format: "In-Person Half-Day Intensive + 2-Week Synthesis",
    icon: Briefcase,
  },
  {
    name: "Acquisition Risk Read",
    audience: "Strategic & Financial Buyers",
    objective: "Uncover operational & integration risks pre-close.",
    deliverables: ["Operational Transferability Scan", "Key-Person Risk Mapping", "Diligence Question Blueprint"],
    format: "Independent Target Audit & Stakeholder Diagnostic",
    icon: ScanSearch,
  },
  {
    name: "Succession Readiness Workshops",
    audience: "Economic Development Partners",
    objective: "Host an in-person workshop helping local owners understand their transition options.",
    deliverables: ["In-Person Workshop Facilitation", "Transition Pathways Education", "Readiness Gap Identification"],
    format: "In-Person Pilot Workshop · Virtual / Hybrid Optional",
    icon: Users,
  },
];

const methodology = [
  {
    n: "01",
    title: "Founder Dependency",
    body: "Auditing what decisions, approvals, sales pipelines, and key relationships rely strictly on the owner’s personal presence.",
    icon: KeyRound,
  },
  {
    n: "02",
    title: "Operational Readiness",
    body: "Evaluating whether existing workflows are documented, replicable, and manageable by a secondary leadership layer.",
    icon: Network,
  },
  {
    n: "03",
    title: "Stakeholder Continuity",
    body: "Assessing the stability and confidence of employees, major clients, suppliers, and lenders throughout the transition window.",
    icon: Users,
  },
];

const inactionCosts = [
  { icon: AlertTriangle, text: "Buyer hesitation during due diligence." },
  { icon: TrendingDown, text: "Steep discounts on your final business valuation." },
  { icon: ClipboardList, text: "Restrictive, multi-year earn-out clauses in deal terms." },
  { icon: HeartPulse, text: "Catastrophic operational failure in a sudden health crisis." },
];

const HomePage = () => {
  return (
    <main data-testid="home-page" className="pt-20">
      {/* HERO */}
      <section data-testid="hero-section" className="relative bg-cream overflow-hidden">
        <div className="grid-lines" />
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28 relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <div className="lg:col-span-7">
              <span className="eyebrow block mb-6 animate-fade-up">Succession &amp; Acquisition Risk Mitigation</span>
              <h1 className="font-serif hero-title text-4xl md:text-5xl lg:text-[3.8rem] leading-[1.08] text-ink-deep animate-fade-up delay-100">
                Is the business ready to <span className="italic text-brand">survive a change in control?</span>
              </h1>
              <p className="mt-7 text-lg md:text-xl text-[#475569] max-w-2xl animate-fade-up delay-200">
                Financials tell you what happened in the past. They don’t tell you what will happen when the
                founder steps back.
              </p>
              <p className="mt-4 text-base text-[#64748B] max-w-2xl animate-fade-up delay-300">
                Cultural Capital Analytics provides transferability intelligence for Canadian business successions
                and acquisitions — helping owners, buyers, and advisors identify the founder-dependency, continuity,
                and transition risks that can affect value before capital or equity changes hands.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-4 animate-fade-up delay-400">
                <RouteButton to="/owners" label="Secure My Succession" variant="dark" testId="hero-cta-owners" />
                <RouteButton to="/buyers" label="De-Risk My Deal" variant="ghost" testId="hero-cta-buyers" />
              </div>
            </div>
            <div className="lg:col-span-5 relative animate-fade-in delay-300">
              <div className="aspect-[4/5] overflow-hidden border border-[#E2E8F0] bg-[#EEF2F6]">
                <img src={IMAGES.heroArch} alt="Abstract architecture" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-4 md:-left-8 bg-ink-deep text-white p-6 shadow-xl max-w-[15rem]">
                <span className="text-xs uppercase tracking-[0.2em] text-brand-light block mb-2">Canadian Transition Wave</span>
                <span className="font-serif text-4xl">$2T+</span>
                <p className="text-sm text-[#94A3B8] mt-2">in business assets changing hands this decade.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PULL QUOTE BAND */}
      <section className="bg-ink-deep">
        <div className="max-w-5xl mx-auto px-5 md:px-10 py-20 md:py-24 text-center">
          <Quote className="w-10 h-10 text-brand mx-auto mb-6" />
          <Reveal>
            <blockquote className="font-serif text-2xl md:text-4xl leading-snug text-white">
              “Most transitions don’t fail on tax or legal structures — they collapse because the value is
              <span className="text-brand-light"> trapped inside the founder’s head.</span>”
            </blockquote>
          </Reveal>
        </div>
      </section>

      {/* MARKET CONTEXT */}
      <section data-testid="market-context" className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Eyebrow>Market Context</Eyebrow>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight text-ink-deep">
                The Canadian Transition Wave: risk on both sides of the table.
              </h2>
            </div>
            <div className="lg:col-span-8">
              <p className="text-lg text-[#475569] mb-10">
                Across Canada, thousands of highly profitable, owner-led businesses are approaching a massive
                generational handoff — yet the vast majority have no formal, written succession plan. That creates
                acute operational risk on both sides of every deal.
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                <Reveal className="bg-cream border border-[#E2E8F0] p-7">
                  <span className="eyebrow block mb-3">For Owners</span>
                  <p className="text-[#475569]">
                    A lack of readiness means fewer qualified buyers, discounted valuations, aggressive earn-out
                    structures, or a business that collapses the moment they step away.
                  </p>
                </Reveal>
                <Reveal delay={120} className="bg-cream border border-[#E2E8F0] p-7">
                  <span className="eyebrow block mb-3">For Buyers</span>
                  <p className="text-[#475569]">
                    Traditional financial diligence can miss a critical reality — that the company’s true value is
                    entirely trapped inside the founder’s relationships and daily micromanagement.
                  </p>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CORE THESIS */}
      <section data-testid="core-thesis" className="bg-cream py-20 md:py-28 relative">
        <div className="grid-lines" />
        <div className="max-w-7xl mx-auto px-5 md:px-10 relative z-10">
          <div className="max-w-3xl mb-12">
            <Eyebrow>Core Thesis</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight text-ink-deep">
              Financial value is only part of the story.
            </h2>
            <p className="mt-5 text-lg text-[#475569]">
              A business can boast exceptional cash flow on paper and still be a highly volatile asset to transfer.
              True operational transferability breaks down when:
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-5 max-w-5xl">
            {thesisBreakdowns.map((t, i) => (
              <Reveal key={i} delay={i * 60} className="flex items-start gap-4 border-b border-[#E2E8F0] pb-5">
                <span className="font-serif text-2xl text-brand leading-none">0{i + 1}</span>
                <p className="text-[#334155]">{t}</p>
              </Reveal>
            ))}
          </div>
          <p className="mt-10 text-[#64748B] italic max-w-3xl">
            These are not soft, operational issues. These are severe transferability risks that directly devalue an
            enterprise during diligence, financing, and post-close integration.
          </p>
        </div>
      </section>

      {/* STRATEGIC POSITIONING */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-3xl mb-14">
            <Eyebrow>Strategic Positioning</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight text-ink-deep">
              What is Business Transferability Intelligence?
            </h2>
            <p className="mt-5 text-lg text-[#475569]">
              Cultural Capital sits squarely before the transaction, the crisis, or the formal estate plan — an
              objective front-end intelligence layer.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { t: "For Owners", b: "We uncover what will reduce buyer interest, lower your valuation, or derail your exit — while you still have time to fix it.", icon: Briefcase },
              { t: "For Buyers", b: "We run specialized buy-side diligence to evaluate what risks will emerge after the founder exits — before you commit capital.", icon: ShieldCheck },
              { t: "For Advisors", b: "We deliver organized, realistic, prepared clients to your desk — making legal, tax, and M&A work cleaner and faster.", icon: ClipboardList },
            ].map((c, i) => (
              <Reveal key={i} delay={i * 100} className="group border border-[#E2E8F0] p-8 hover:border-brand transition-colors bg-cream">
                <c.icon className="w-8 h-8 text-brand mb-6" />
                <h3 className="font-serif text-xl text-ink-deep mb-3">{c.t}</h3>
                <p className="text-[#475569]">{c.b}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DUAL FUNNEL — CHOOSE YOUR PATHWAY */}
      <section data-testid="dual-funnel" className="bg-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Eyebrow className="mx-auto w-fit">Choose Your Pathway</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl text-ink-deep">Two tracks. One objective: a deal that survives the handoff.</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Owners */}
            <Reveal className="flex flex-col bg-white border border-[#E2E8F0] p-9 hover:shadow-xl transition-shadow">
              <span className="eyebrow block mb-4">For Business Owners</span>
              <h3 className="font-serif text-2xl md:text-3xl text-ink-deep mb-4">Protect the value of what you’ve built.</h3>
              <p className="text-[#475569] mb-6">
                Whether you want to sell to a competitor, execute a management buyout, transition to family, or set
                up an Employee Ownership Trust, our Succession Roadmap Sprint strips away the ambiguity — an in-person,
                half-day intensive that delivers a concrete, advisor-ready plan.
              </p>
              <div className="mt-auto pt-4">
                <RouteButton to="/owners" label="Explore the Succession Roadmap Sprint" variant="dark" testId="funnel-owners" />
              </div>
            </Reveal>
            {/* Buyers */}
            <Reveal delay={120} className="flex flex-col bg-ink-deep text-white p-9 hover:shadow-xl transition-shadow">
              <span className="eyebrow eyebrow-light block mb-4">For Corporate Buyers</span>
              <h3 className="font-serif text-2xl md:text-3xl mb-4">Know exactly what you are purchasing.</h3>
              <p className="text-[#CBD5E1] mb-6">
                In founder-led companies, the biggest liabilities don’t show up on a balance sheet. Our Acquisition
                Risk Read acts as an operational health check — a crystal-clear look at post-close integration risks,
                key-person dependencies, and structural transferability.
              </p>
              <div className="mt-auto pt-4">
                <RouteButton to="/buyers" label="Explore the Acquisition Risk Read" variant="light" testId="funnel-buyers" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CORE SERVICES */}
      <section data-testid="core-services" className="bg-ink-deep py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-14">
            <Eyebrow light>Our Core Services</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl text-white">Three engagements, calibrated to your seat at the table.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <Reveal key={i} delay={i * 100} className="flex flex-col border border-[#1E293B] bg-[#162236] p-8">
                <s.icon className="w-8 h-8 text-brand-light mb-6" />
                <h3 className="font-serif text-2xl text-white mb-1">{s.name}</h3>
                <p className="text-sm text-brand-light mb-6">{s.audience}</p>
                <p className="text-[#CBD5E1] mb-6">{s.objective}</p>
                <p className="text-xs uppercase tracking-[0.18em] text-[#64748B] mb-3">Key Deliverables</p>
                <ul className="space-y-2 mb-6">
                  {s.deliverables.map((d, j) => (
                    <li key={j} className="flex items-start gap-2 text-[#CBD5E1] text-sm">
                      <ArrowRight className="w-4 h-4 text-brand mt-0.5 shrink-0" /> {d}
                    </li>
                  ))}
                </ul>
                <p className="mt-auto pt-4 border-t border-[#1E293B] text-sm text-[#94A3B8]">{s.format}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* METHODOLOGY */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-14">
            <Eyebrow>Our Methodology</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl text-ink-deep">What we look for.</h2>
            <p className="mt-4 text-[#475569]">We systematically analyze the hidden structural factors that dictate asset transferability.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {methodology.map((m, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="flex items-center gap-4 mb-5">
                  <span className="font-serif text-5xl text-[#E2E8F0]">{m.n}</span>
                  <m.icon className="w-7 h-7 text-brand" />
                </div>
                <h3 className="font-serif text-xl text-ink-deep mb-3">{m.title}</h3>
                <p className="text-[#475569]">{m.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <Eyebrow>Why It Matters</Eyebrow>
              <h2 className="font-serif text-3xl md:text-4xl text-ink-deep mb-5">The real cost of inaction.</h2>
              <p className="text-[#475569]">
                Founder dependency is an expensive liability. If a company cannot function seamlessly without its
                creator, that structural flaw will reveal itself — usually at the worst possible moment.
              </p>
            </div>
            <div className="lg:col-span-7 space-y-4">
              {inactionCosts.map((c, i) => (
                <Reveal key={i} delay={i * 80} className="flex items-center gap-5 bg-white border border-[#E2E8F0] p-5">
                  <span className="shrink-0 w-11 h-11 flex items-center justify-center bg-cream border border-[#E2E8F0]">
                    <c.icon className="w-5 h-5 text-brand" />
                  </span>
                  <p className="text-[#334155]">{c.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-ink-deep py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <h2 className="font-serif text-3xl md:text-5xl text-white leading-tight">
            Before the business changes hands, understand what can actually transfer.
          </h2>
          <p className="mt-6 text-lg text-[#94A3B8]">
            Expose the vital human and operational risks that spreadsheets alone cannot show.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
            <RouteButton to="/owners" label="For Owners" variant="light" testId="final-owners" />
            <RouteButton to="/buyers" label="For Buyers" variant="accent" testId="final-buyers" />
            <RouteButton to="/workshops" label="Workshops" variant="outlineLight" testId="final-workshops" />
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
